import { TICKET_NUMBER_SORT_ID } from "@/lib/boardSort";
import type { FilterCond, SortSpec } from "@/lib/types";

/**
 * Per-USER, per-board persistence for the "what am I currently looking at" view controls:
 * column filters and the single-column sort. These used to live in the board's shared
 * `view_config` (a 2026-06-16 "shared, not personal" call), which meant one person's filter
 * was imposed on every colleague viewing the board. They now live in localStorage keyed per
 * board, matching the sibling `flow.*.<id>` per-user keys already in BoardSurface
 * (`flow.year.tab.<id>`, `flow.groups.collapsed.<id>`, `flow.tree.expanded.<id>`).
 *
 * Grouping, kanban-column and hidden-columns intentionally STAY shared on the board — they
 * are board structure, not a personal view — so they are not handled here.
 *
 * Pure serialize/validate/resolve helpers are unit-tested + mutation-checked; the load/save
 * wrappers add guarded localStorage I/O (private mode / quota can never throw into the UI).
 */

export function filtersKey(boardId: string): string {
  return `flow.board.filters.${boardId}`;
}
export function sortKey(boardId: string): string {
  return `flow.board.sort.${boardId}`;
}

function isFilterCond(v: unknown): v is FilterCond {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return typeof r.column_id === "string" && typeof r.op === "string" && "value" in r;
}

function isSortSpec(v: unknown): v is SortSpec {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return typeof r.column_id === "string" && (r.dir === "asc" || r.dir === "desc");
}

/** Parse a stored filters array. Garbage / non-array / malformed entries degrade to []. */
export function deserializeFilters(raw: string | null): FilterCond[] {
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  return Array.isArray(parsed) ? parsed.filter(isFilterCond) : [];
}

/**
 * Parse a stored sort. The three states are distinct and load-bearing:
 *   - `undefined` — the key is ABSENT: the user never set a sort → callers apply the default.
 *   - `null`      — the user explicitly turned sorting OFF → do not fall back to the default.
 *   - SortSpec    — that sort.
 * Any garbage (unparseable, wrong shape, bad dir) is treated as "never set" (undefined).
 */
export function deserializeSort(raw: string | null): SortSpec | null | undefined {
  if (raw == null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null) return null;
  return isSortSpec(parsed) ? parsed : undefined;
}

/**
 * Resolve the effective sort from what was stored, preserving the exact semantics the shared
 * `view_config` path had: an explicit stored value (null = off, or a spec) wins; otherwise a
 * ticket board defaults to newest-first and everything else to no sort.
 */
export function resolveInitialSort(
  stored: SortSpec | null | undefined,
  ticketBoard: boolean,
): SortSpec | null {
  if (stored !== undefined) return stored;
  return ticketBoard ? { column_id: TICKET_NUMBER_SORT_ID, dir: "desc" } : null;
}

export function loadFilters(boardId: string): FilterCond[] {
  try {
    return deserializeFilters(localStorage.getItem(filtersKey(boardId)));
  } catch {
    return [];
  }
}

export function saveFilters(boardId: string, filters: FilterCond[]): void {
  try {
    // No active filters → remove the key so storage stays clean and load returns [].
    if (filters.length === 0) localStorage.removeItem(filtersKey(boardId));
    else localStorage.setItem(filtersKey(boardId), JSON.stringify(filters));
  } catch {
    // best-effort persistence — never fatal to filtering.
  }
}

export function loadStoredSort(boardId: string): SortSpec | null | undefined {
  try {
    return deserializeSort(localStorage.getItem(sortKey(boardId)));
  } catch {
    return undefined;
  }
}

export function saveSort(boardId: string, sort: SortSpec | null): void {
  try {
    // Always write — a stored `null` ("off") must stay distinct from an absent key
    // ("never set"), so we never removeItem here.
    localStorage.setItem(sortKey(boardId), JSON.stringify(sort));
  } catch {
    // best-effort — see saveFilters.
  }
}
